import "bootstrap/dist/js/bootstrap.bundle.min.js"
import "@fortawesome/fontawesome-free/css/all.css"
import "react-toastify/dist/ReactToastify.css"
import "../styles/darkmode.css"
import "../styles/misc.css"

import { useEffect, useState } from "react"

import { initAdminLiteShim } from "../ppAdminLiteShim"

import { Routes, Route, useLocation, useNavigate } from "react-router-dom"

import PPLoginPage from "./PPLoginPage"
import { RightTabContent } from "../components/righttab/PPRightTab"
import useRequireAuth from "../hooks/use-require-auth"
import { TopRightToolbar } from "../components/layout/TopRightToolbar"
import { SearchBar } from "../components/layout/Searchbar"
import PPUtils from "../utils/PPUtils"
import { parsePositiveInt, readStorageItem, writeStorageItem } from "../pricemanagement/common/tableStateStorage"
import { isLoggedIn } from "../hooks/use-auth"
import { useSubscription } from "@apollo/client"
import { ToastContainer, toast } from "react-toastify"
import INSTANT_CRAWL_SUBSCRIPTION from "../utils/const/InstantCrawlSub"
import ExportSubscription from "../utils/const/ExportSub"
import ExportToastMsg from "../utils/const/ExportToastMsg"
import { useTranslation } from "react-i18next"
import PPPrimeSidebar from "../components/layout/PPPrimeSidebar"
import MappingState from "../utils/const/MappingState"
import PPAIStandalone from "./ai/PPAIStandalone"
import { isStandalone } from "../config/ppAiConfig"
import { appInnerRouteList } from "./AppInnerRoutes"
import PPErrorBoundary from "../components/layout/PPErrorBoundary"

initAdminLiteShim()

const SETTING_LOCALSTORAGE = {
    PPPriceSettings: [
        { name: "switchPriceAge", defaultValue: false },
        { name: "switchStock", defaultValue: true },
        { name: "switchCustomerStock", defaultValue: false },
        { name: "switchCurrency", defaultValue: false },
        { name: "mappingState", defaultValue: MappingState.MAPPED_AND_UNMAPPED },
        { name: "sortRanking", defaultValue: false },
        { name: "showTotalPrice", defaultValue: false },
        { name: "showLongShopname", defaultValue: false },
        { name: "showPriceDifferenceThumb", defaultValue: true },
        { name: "default_language_stats", defaultValue: "de" },
        { name: "dashboard_topproducts_own_nrs", defaultValue: "" },
        { name: "switchShowLinks", defaultValue: false },
        { name: "switchDiscontinuedItems", defaultValue: false }
    ]
}

function searchwordFromPath(pathname) {
    const prefix = "/pricetable/s/"
    if (!pathname.startsWith(prefix)) return ""
    const raw = pathname.slice(prefix.length)
    if (!raw) return ""
    try {
        return decodeURIComponent(raw).replace(/\|\|/g, "/")
    } catch {
        return raw.replace(/\|\|/g, "/")
    }
}

function App() {
    const [t, i18n] = useTranslation()
    const location = useLocation()
    const navigate = useNavigate()
    const searchword = searchwordFromPath(location.pathname)
    const [selectedProductCell, setSelectedProductCell] = useState()
    const [selectedRowInfos, setSelectedRowInfos] = useState()
    const [refetchPricetable, setRefetchPricetable] = useState()
    const [productCount, setProductCount] = useState("")
    const [lightMode, setLightMode] = useState(!PPUtils.activDarkmode())
    const [presetId, setPresetId] = useState(null)
    const [dataCurrentAuth, setDataCurrentAuth] = useState()

    const [authName, setAuthName] = useState("")
    const [authAvatarUrl, setAuthAvatarUrl] = useState("")

    const handleSubmit = (event) => {
        event.preventDefault()
        const value = event.target.searchinput.value
        if (value.length > 0) {
            const searchValue = value.replace(/\//g, '||')
            navigate("/pricetable/s/" + encodeURIComponent(searchValue))
        } else {
            navigate("/pricetable/")
        }
    }
    const auth = useRequireAuth()
    const currentAuth = PPUtils.getCurrentAuthFromAuth(auth)
    const hasPriceManagementAccess = PPUtils.hasPriceManagementAccess(currentAuth?.user?.ruleEngineAccess)

    const userId = currentAuth?.user?.id
    const subscriptionsActive = !isStandalone() && !!userId && isLoggedIn(auth.login) && !auth.isExpired()

    useEffect(() => {
        if (currentAuth) {
            let title = currentAuth?.customer?.name ? " - " + currentAuth?.customer?.name : ""
            document.title = t("pp.title") + title
        }
        if (currentAuth?.user?.configLanguage && i18n.language !== currentAuth.user.configLanguage) {
            i18n.changeLanguage(currentAuth.user.configLanguage)
        }

        const storedPresetId = parsePositiveInt(presetId)
        if (storedPresetId != null) {
            writeStorageItem("presetId", String(storedPresetId))
        } else if (presetId == null) {
            const fromStorage = parsePositiveInt(readStorageItem("presetId"))
            if (fromStorage != null) {
                setPresetId(fromStorage)
            }
        }

        if (currentAuth?.user?.name) {
            setAuthName(currentAuth?.user?.name)
        }
        if (currentAuth?.user?.avatarUrl != "") {
            setAuthAvatarUrl(currentAuth?.user?.avatarUrl)
        } else {
            setAuthAvatarUrl("")
        }
    }, [currentAuth, presetId])

    useEffect(() => {
        if (currentAuth && dataCurrentAuth) {
            if (dataCurrentAuth.name) {
                setAuthName(dataCurrentAuth.name)
            }
            if (dataCurrentAuth.avatarUrl != "") {
                setAuthAvatarUrl(dataCurrentAuth.avatarUrl)
            } else {
                setAuthAvatarUrl("")
            }
        }
    }, [dataCurrentAuth])

    useEffect(() => {
        if (!auth.login || auth.isExpired()) return
        PPUtils.setGenericTranslation(auth, i18n)
        PPUtils.setSettingsLocalstorage(SETTING_LOCALSTORAGE)
    }, [auth.login, i18n])

    const notifySuccess = (msg) => {
        toast(msg)
    }
    const notifyError = (msg) => {
        toast.error(msg)
    }

    useSubscription(INSTANT_CRAWL_SUBSCRIPTION, {
        skip: !subscriptionsActive,
        variables: { channel: "InstantCrawls" + userId },
        onData: ({ data: { data } }) => {
            if (data !== undefined && data.instantCrawlSub.responseCode === 200) {
                notifySuccess(t("subscription.instantcrawl_ready", { productnr: data.instantCrawlSub.productnr }))
                document.getElementById("sidebarclose")?.click()
                refetchPricetable?.refetch()
            } else if (data !== undefined && data.instantCrawlSub.responseCode === 500) {
                notifyError(
                    t("subscription.instantcrawl_failed", {
                        productnr: data.instantCrawlSub.productnr,
                        responseErrorMessage: data.instantCrawlSub.responseErrorMessage
                    })
                )
            }
        }
    })

    useSubscription(ExportSubscription.GQL.EXPORTS_SUBSCRIPTION, {
        skip: !subscriptionsActive,
        variables: { channel: "Exports" + userId },
        onData: ({ data: { data } }) => {
            if (data !== undefined && data.exportSub.state === ExportSubscription.States.READY) {
                toast(<ExportToastMsg exportData={data.exportSub} />, {
                    autoClose: false,
                    closeOnClick: false
                })
            } else if (data !== undefined && data.exportSub.state === ExportSubscription.States.FAILED) {
                notifyError(t("subscription.export_failed", { name: data.exportSub.name }))
            }
        }
    })

    useEffect(() => {
        document.querySelectorAll('[data-bs-toggle="tooltip"]').forEach((el) => {
            try {
                window.bootstrap?.Tooltip?.getOrCreateInstance?.(el)
            } catch {
                // Tooltip-Init optional
            }
        })
    }, [location.pathname])

    if (location.pathname === "/ppai-standalone" || isStandalone()) {
        return <PPAIStandalone />
    }
    if (!auth.login || auth.isExpired()) {
        return <PPLoginPage changePresetId={setPresetId} />
    }

    return (
        <Routes>
            <Route path="/loginpage" element={<PPLoginPage changePresetId={setPresetId} />} />

            <Route
                path="*"
                element={
                    <div className="wrapper">
                        <nav className={"main-header navbar navbar-expand navbar-white navbar-light"}>
                            <ul className="navbar-nav">
                                <li className="nav-item">
                                    <button type="button" className="nav-link btn" data-widget="pushmenu">
                                        <i className="fas fa-bars" />
                                    </button>
                                </li>
                            </ul>

                            <SearchBar
                                onSubmit={handleSubmit}
                                searchword={searchword}
                                setLightMode={setLightMode}
                                lightMode={lightMode}
                            />
                            <TopRightToolbar customerLogo={currentAuth?.customer?.logoUrl} />
                        </nav>

                        <PPPrimeSidebar
                            lightMode={lightMode}
                            setPresetId={setPresetId}
                            auth={auth}
                            authName={authName}
                            authAvatarUrl={authAvatarUrl}
                            productCount={productCount}
                            hasPriceManagementAccess={hasPriceManagementAccess}
                        />

                        <div className="content-wrapper">
                            <div className="content pt-3 pb-2">
                                <div className="container-fluid">
                                    <PPErrorBoundary variant="page" resetKeys={[location.pathname, location.search]}>
                                        <Routes>
                                            {appInnerRouteList({
                                                userId,
                                                currentAuth,
                                                auth,
                                                setSelectedProductCell,
                                                setSelectedRowInfos,
                                                setProductCount,
                                                setPresetId,
                                                presetId,
                                                setRefetchPricetable,
                                                settingLocalstorage: SETTING_LOCALSTORAGE,
                                                setDataCurrentAuth,
                                                hasPriceManagementAccess
                                            })}
                                        </Routes>
                                    </PPErrorBoundary>
                                </div>
                            </div>
                        </div>
                        <aside
                            id="rightsidebar"
                            className="control-sidebar control-sidebar-light layout-fixed bg-light">
                            <PPErrorBoundary variant="section" resetKeys={[location.pathname]}>
                                <RightTabContent
                                    selectedProductCell={selectedProductCell}
                                    selectedProductRow={selectedRowInfos}
                                    presetId={presetId}
                                    customerId={currentAuth?.user?.customerId}
                                    userId={userId}
                                    refetchPricetable={refetchPricetable}
                                />
                            </PPErrorBoundary>
                        </aside>
                        <ToastContainer />
                    </div>
                }
            />
        </Routes>
    )
}

export default App
